import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { AuthService } from '@auth0/auth0-angular';
import { MessageService } from 'primeng/api';
import { bmxurl, testToolsAllowed } from 'src/globals';
import { GlobalService } from '../_services/global.service';
import { BusyService } from '../_services/busy.service';
import { resource } from 'src/types/resource';
import { checkAll, CopyRow, EmptyContainer, findAllEmptyContainers, Holder, loadBothLists } from './copyfinder';

/*
  Resource Test - only for the login in environment.testToolsEmail (see testToolsAllowed in globals.ts).

  Containers and resource groups hold their own copies of each resource rather than a reference,
  so editing a resource does not reach those copies. This page checks every resource in turn
  against every container and group, at any depth (a group can hold a container that holds the
  resource), and lists the copies whose concurrentUses has drifted. It also lists containers with
  no resources - the container itself or a copy of it in a group - as they break the booking
  process. See copyfinder.ts for how.
*/
@Component({
  selector: 'app-resourcetest',
  templateUrl: './resourcetest.component.html',
  styleUrls: ['./resourcetest.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ResourceTestComponent implements OnInit {
  //Only for the login in testToolsEmail - known once Auth0 has said who is logged in
  available: boolean = false;
  checked: boolean = false;

  VenueID: string = localStorage.getItem("SmartVenueID")!;

  resources: resource[] = [];
  holders: Holder[] = [];
  failedLoads: string[] = [];
  loaded: boolean = false;
  rows: CopyRow[] = [];
  empty: EmptyContainer[] = [];

  constructor(private http: HttpClient, private messageService: MessageService, private globalService: GlobalService, private busyService: BusyService, private auth: AuthService) { }

  ngOnInit(): void {
    this.globalService.setSmallScreenMenu(false);

    this.auth.user$.pipe(take(1)).subscribe(user => {
      this.available = testToolsAllowed(user?.email);
      this.checked = true;

      if (this.available)
        this.load();
    });
  }

  load() {
    this.busyService.busy();

    forkJoin({
      resources: loadBothLists(this.http, bmxurl + "ResourceList/" + this.VenueID + "//", 'resourceId'),
      groups: loadBothLists(this.http, bmxurl + "ResourceGroupNameList/" + this.VenueID + "/", 'resourcegroupnameId')
    }).subscribe(lists => {
      if (lists.resources == null || lists.groups == null) {
        this.busyService.idle();
        this.messageService.add({severity:'error', summary:'Not Loaded', detail:'The resource or resource group list could not be loaded.'});
        return;
      }

      this.resources = [...lists.resources].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

      //The lists may not carry everything a container or group holds, so fetch each one in full
      const fetches: Observable<Holder | null>[] = [];

      this.resources.filter(r => r.isresourcecontainer).forEach(r => {
        fetches.push(this.fetchHolder(bmxurl + "Resource/" + this.VenueID + "/" + r.resourceId, 'Container', r.name, r.disabled));
      });

      lists.groups.forEach(g => {
        fetches.push(this.fetchHolder(bmxurl + "resourcegroupname/" + this.VenueID + "/" + g.resourcegroupnameId, 'Resource Group', g.name, g.disabled));
      });

      (fetches.length == 0 ? of([]) : forkJoin(fetches)).subscribe(holders => {
        this.holders = (holders.filter(h => h != null) as Holder[])
          .sort((a, b) => a.kind.localeCompare(b.kind) || (a.name ?? '').localeCompare(b.name ?? ''));
        this.rows = checkAll(this.resources, this.holders);
        this.empty = findAllEmptyContainers(this.holders);
        this.loaded = true;
        this.busyService.idle();
      });
    });
  }

  //A failed fetch is noted and skipped, so one bad container does not stop the rest being checked
  fetchHolder(url: string, kind: string, name: string, disabled: boolean): Observable<Holder | null> {
    return this.http.get<any>(url).pipe(
      map(response => {
        if (!response?.succeeded || response.data == null) {
          this.failedLoads.push(kind + ': ' + name);
          return null;
        }
        return { kind, name, disabled, data: response.data } as Holder;
      }),
      catchError(() => {
        this.failedLoads.push(kind + ': ' + name);
        return of(null);
      })
    );
  }
}
